import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { 
  CheckCircle, Clock, XCircle, FileText, AlertTriangle, 
  Calendar, Building2, Briefcase, FileCheck
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PublicProgress({ type: propType }) {
  const { shareId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  // Determine type from prop or URL path
  const type = propType || (window.location.pathname.includes('/impl/') ? 'impl' : 'app');

  useEffect(() => {
    const fetchProgress = async () => {
      try {
        const endpoint = type === 'impl' 
          ? `${API}/api/implementation/progress/${shareId}`
          : `${API}/api/progress/${shareId}`;
        
        const response = await axios.get(endpoint);
        setData(response.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Το έργο δεν βρέθηκε');
      } finally {
        setLoading(false);
      }
    };

    fetchProgress();
  }, [shareId, type]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="h-5 w-5 text-emerald-500" />;
      case 'uploaded':
        return <FileCheck className="h-5 w-5 text-blue-500" />;
      case 'rejected':
        return <XCircle className="h-5 w-5 text-rose-500" />;
      default:
        return <Clock className="h-5 w-5 text-amber-500" />;
    }
  };

  const getStatusBg = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-emerald-50 border-emerald-200';
      case 'uploaded':
        return 'bg-blue-50 border-blue-200';
      case 'rejected':
        return 'bg-rose-50 border-rose-200';
      default:
        return 'bg-amber-50 border-amber-200';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-slate-600">Φόρτωση...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-sm border p-8 text-center max-w-md">
          <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Σφάλμα</h2>
          <p className="text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="flex items-center gap-2 text-cyan-100 text-sm mb-2">
            {data.project_type === 'implementation' ? (
              <Briefcase className="h-4 w-4" />
            ) : (
              <FileText className="h-4 w-4" />
            )}
            <span>{data.project_type_label}</span>
          </div>
          <h1 className="text-2xl md:text-3xl font-bold mb-2">{data.project_title}</h1>
          <div className="flex items-center gap-4 text-cyan-100">
            <span className="flex items-center gap-1">
              <Building2 className="h-4 w-4" />
              {data.client_name}
            </span>
            {data.deadline && (
              <span className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                Προθεσμία: {new Date(data.deadline).toLocaleDateString('el-GR')}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Progress Card */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-900">Πρόοδος Έργου</h2>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              data.status === 'completed' 
                ? 'bg-emerald-100 text-emerald-700' 
                : 'bg-blue-100 text-blue-700'
            }`}>
              {data.status_label}
            </span>
          </div>

          {/* Progress Bar */}
          <div className="mb-6">
            <div className="flex justify-between text-sm text-slate-600 mb-2">
              <span>Ολοκλήρωση</span>
              <span className="font-semibold">{data.progress}%</span>
            </div>
            <div className="h-4 bg-slate-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 transition-all duration-500"
                style={{ width: `${data.progress}%` }}
              />
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-3 bg-slate-50 rounded-lg">
              <div className="text-2xl font-bold text-slate-900">{data.total_documents}</div>
              <div className="text-sm text-slate-600">Σύνολο</div>
            </div>
            <div className="text-center p-3 bg-emerald-50 rounded-lg">
              <div className="text-2xl font-bold text-emerald-600">{data.completed_documents}</div>
              <div className="text-sm text-emerald-700">Ολοκληρωμένα</div>
            </div>
            <div className="text-center p-3 bg-amber-50 rounded-lg">
              <div className="text-2xl font-bold text-amber-600">{data.pending_documents}</div>
              <div className="text-sm text-amber-700">Εκκρεμούν</div>
            </div>
            <div className="text-center p-3 bg-rose-50 rounded-lg">
              <div className="text-2xl font-bold text-rose-600">{data.rejected_documents}</div>
              <div className="text-sm text-rose-700">Απορρίφθηκαν</div>
            </div>
          </div>
        </div>

        {/* Documents List - Application */}
        {data.project_type === 'application' && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
              <h2 className="text-lg font-semibold text-slate-900">Δικαιολογητικά</h2>
            </div>
            <div className="divide-y divide-slate-100">
              {data.documents.map((doc, idx) => (
                <div 
                  key={idx} 
                  className={`p-4 flex items-start gap-4 ${getStatusBg(doc.status)}`}
                >
                  <div className="flex-shrink-0 mt-0.5">
                    {getStatusIcon(doc.status)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-slate-900">{doc.document_name}</h3>
                      {doc.has_signed_file && (
                        <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full">
                          Υπογεγραμμένο
                        </span>
                      )}
                    </div>
                    {doc.description && (
                      <p className="text-sm text-slate-500 mt-0.5">{doc.description}</p>
                    )}
                    {doc.status === 'rejected' && doc.review_comment && (
                      <p className="text-sm text-rose-600 mt-1 flex items-start gap-1">
                        <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                        {doc.review_comment}
                      </p>
                    )}
                  </div>
                  <div className="flex-shrink-0">
                    <span className={`text-sm font-medium ${
                      doc.status === 'approved' ? 'text-emerald-600' :
                      doc.status === 'uploaded' ? 'text-blue-600' :
                      doc.status === 'rejected' ? 'text-rose-600' :
                      'text-amber-600'
                    }`}>
                      {doc.status_label}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Documents List - Implementation */}
        {data.project_type === 'implementation' && (
          <>
            {/* Invoices */}
            {data.invoices && data.invoices.length > 0 && (
              <div className="space-y-4 mb-6">
                {data.invoices.map((invoice, idx) => (
                  <div key={idx} className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                    <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-slate-900">{invoice.invoice_number}</h3>
                        <p className="text-sm text-slate-500">{invoice.expense_category}</p>
                      </div>
                      <div className="text-right">
                        {invoice.net_amount && (
                          <div className="text-sm text-slate-600">
                            Καθαρό: {invoice.net_amount.toLocaleString('el-GR', { style: 'currency', currency: 'EUR' })}
                          </div>
                        )}
                        <div className="text-xs text-slate-500">
                          {invoice.completed_count}/{invoice.document_count} δικαιολογητικά
                        </div>
                      </div>
                    </div>
                    <div className="divide-y divide-slate-100">
                      {invoice.documents.map((doc, docIdx) => (
                        <div 
                          key={docIdx} 
                          className={`p-4 flex items-start gap-4 ${getStatusBg(doc.status)}`}
                        >
                          <div className="flex-shrink-0 mt-0.5">
                            {getStatusIcon(doc.status)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium text-slate-900">{doc.document_name}</h4>
                              {doc.has_signed_file && (
                                <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full">
                                  Υπογεγραμμένο
                                </span>
                              )}
                            </div>
                            {doc.status === 'rejected' && doc.review_comment && (
                              <p className="text-sm text-rose-600 mt-1 flex items-start gap-1">
                                <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                                {doc.review_comment}
                              </p>
                            )}
                          </div>
                          <div className="flex-shrink-0">
                            <span className={`text-sm font-medium ${
                              doc.status === 'approved' ? 'text-emerald-600' :
                              doc.status === 'uploaded' ? 'text-blue-600' :
                              doc.status === 'rejected' ? 'text-rose-600' :
                              'text-amber-600'
                            }`}>
                              {doc.status_label}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* General Documents */}
            {data.general_documents && data.general_documents.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
                  <h2 className="text-lg font-semibold text-slate-900">Γενικά Έγγραφα</h2>
                </div>
                <div className="divide-y divide-slate-100">
                  {data.general_documents.map((doc, idx) => (
                    <div 
                      key={idx} 
                      className={`p-4 flex items-start gap-4 ${getStatusBg(doc.status)}`}
                    >
                      <div className="flex-shrink-0 mt-0.5">
                        {getStatusIcon(doc.status)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium text-slate-900">{doc.document_name}</h3>
                          {doc.has_signed_file && (
                            <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full">
                              Υπογεγραμμένο
                            </span>
                          )}
                        </div>
                        {doc.status === 'rejected' && doc.review_comment && (
                          <p className="text-sm text-rose-600 mt-1 flex items-start gap-1">
                            <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                            {doc.review_comment}
                          </p>
                        )}
                      </div>
                      <div className="flex-shrink-0">
                        <span className={`text-sm font-medium ${
                          doc.status === 'approved' ? 'text-emerald-600' :
                          doc.status === 'uploaded' ? 'text-blue-600' :
                          doc.status === 'rejected' ? 'text-rose-600' :
                          'text-amber-600'
                        }`}>
                          {doc.status_label}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-slate-500">
          <p>Αυτή η σελίδα είναι μόνο για ανάγνωση</p>
          <p className="mt-1">ESPA Manager</p>
        </div>
      </div>
    </div>
  );
}
